import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, ArrowLeft, Eye, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { LEGAL_ADMIN_PAGES } from "./AdminLayout";

export default function LegalIndex() {
  // Fetch visibility flags so each card can show its current "shown / hidden" state.
  const [visibility, setVisibility] = useState({});

  useEffect(() => {
    let cancel = false;
    const load = () =>
      api.get("/content/public").then(({ data }) => {
        if (cancel || !data) return;
        const v = {};
        for (const p of LEGAL_ADMIN_PAGES) {
          const slug = p.key.slice("legal.".length);
          v[slug] = data[`legal.${slug}.visible`];
        }
        setVisibility(v);
      });
    load();
    // Refresh when the tab regains focus or admin saves elsewhere.
    const onFocus = () => load();
    const onContentUpdated = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("neptune:content-updated", onContentUpdated);
    return () => {
      cancel = true;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("neptune:content-updated", onContentUpdated);
    };
  }, []);

  return (
    <div className="max-w-5xl">
      <Link
        to="/admin/dashboard"
        data-testid="legal-back-to-dashboard"
        className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Dashboard
      </Link>
      <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Legal</div>
      <h1 className="font-display mt-3 text-3xl sm:text-5xl font-semibold tracking-tighter chrome-text">
        Legal pages
      </h1>
      <p className="mt-4 text-[15px] text-zinc-400 leading-relaxed max-w-xl">
        {LEGAL_ADMIN_PAGES.length} policies. Edit any field, paste your final copy from your Word
        documents, and toggle whether each page appears in the website footer.
      </p>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {LEGAL_ADMIN_PAGES.map((p) => {
          const slug = p.key.slice("legal.".length);
          const v = visibility[slug];
          // Default to true to match Footer behavior when the flag is missing.
          const isVisible = v === undefined ? true : Boolean(v);
          return (
            <Link
              key={p.key}
              to={`/admin/pages/${p.key}`}
              data-testid={`legal-card-${slug}`}
              className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.04] hover:border-white/[0.12] transition-colors p-5"
            >
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">Policy</div>
                <span
                  data-testid={`legal-card-visibility-${slug}`}
                  className={`inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em] ${
                    isVisible ? "text-[#00E5FF]" : "text-zinc-600"
                  }`}
                >
                  {isVisible ? (
                    <>
                      <Eye className="h-3 w-3" /> Visible
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-3 w-3" /> Hidden
                    </>
                  )}
                </span>
              </div>
              <div className="mt-3 flex items-start justify-between gap-3">
                <div className="font-display text-[18px] sm:text-[20px] text-white tracking-tight leading-snug">
                  {p.label}
                </div>
                <ArrowUpRight className="h-4 w-4 text-zinc-500 group-hover:text-[#00E5FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
